import type * as React from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/utils/cn'

export type NavigationRailItem = {
  id: string
  label: string
  icon: React.ReactNode
  /** Render a separator after this item to mark the end of a group. */
  groupEnd?: boolean
  /** Render the item as a nested child of the previous item (smaller, indented). */
  nested?: boolean
  /**
   * If provided, the item renders as an `<a href>` so middle-click and
   * cmd/ctrl-click open in a new tab natively. Plain left-click is still
   * intercepted and routed through `onSelect`.
   */
  href?: string
  /**
   * Item has no destination of its own. When used as a section parent, clicking
   * it toggles the section open without firing `onSelect` for navigation.
   */
  nonNavigable?: boolean
  /**
   * Section parent whose children are never collapsed. The section renders
   * open for as long as the parent is present, and clicking the parent
   * navigates straight away instead of spending the first click on expanding.
   */
  alwaysOpen?: boolean
  /** Optional count chip at the item's top-right (e.g. items awaiting action). */
  badge?: number
  badgeLabel?: string
}

export type NavigationRailVariant = 'floating' | 'static'

type NavigationRailProps = {
  items: NavigationRailItem[]
  /** Items pinned to the bottom of the rail (e.g. profile, notifications). */
  footerItems?: NavigationRailItem[]
  /**
   * Slot rendered at the top of the rail. Typically used for a "case chip"
   * that links back to the cases list when the rail is in case mode.
   */
  header?: React.ReactNode
  activeId?: string
  onSelect?: (id: string) => void
  /**
   * `floating` — rounded card with shadow, sits inside layout (default).
   * `static` — full-height surface flush to the viewport edge, no shadow.
   */
  variant?: NavigationRailVariant
  /** When true, renders each item's label as a caption beneath its icon. */
  showLabels?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * Click handler for an `<a href>` that navigates inside the SPA: middle-click
 * and cmd/ctrl-click keep the browser's own behaviour (open in a new tab), a
 * plain left click is intercepted and handed to `go`.
 */
function spaClick(e: React.MouseEvent, go: () => void) {
  if (e.defaultPrevented) return
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  e.preventDefault()
  go()
}

type ItemButtonProps = {
  item: NavigationRailItem
  isActive: boolean
  /** Item leads a section whose child is the active item — softer highlight. */
  isSectionActive?: boolean
  /** Render the item label as a caption beneath the icon. */
  showLabel?: boolean
  onSelect?: (id: string) => void
}

function ItemButton({ item, isActive, isSectionActive, showLabel, onSelect }: ItemButtonProps) {
  const nested = item.nested
  const labelRef = useRef<HTMLSpanElement>(null)
  const [labelOverflowing, setLabelOverflowing] = useState(false)
  useLayoutEffect(() => {
    if (!showLabel) return
    const el = labelRef.current
    if (!el) return
    const check = () => setLabelOverflowing(el.scrollWidth > el.clientWidth)
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [showLabel, item.label])
  const className = cn(
    'relative flex rounded-xl text-muted-foreground transition-colors outline-none',
    'hover:bg-accent hover:text-foreground',
    'focus-visible:ring-2 focus-visible:ring-ring',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    showLabel
      ? cn(
          'w-full flex-col items-center gap-1 px-1 py-1.5',
          nested ? "[&_svg:not([class*='size-'])]:size-4" : "[&_svg:not([class*='size-'])]:size-5"
        )
      : cn(
          'items-center justify-center',
          nested
            ? "size-8 [&_svg:not([class*='size-'])]:size-4"
            : "size-10 [&_svg:not([class*='size-'])]:size-5"
        ),
    isSectionActive && !isActive && 'bg-sidebar text-primary hover:bg-sidebar hover:text-primary',
    isActive && 'bg-primary/15 text-primary hover:bg-primary/15 hover:text-primary'
  )

  const dataProps = {
    'data-slot': 'navigation-rail-item',
    'data-active': isActive || undefined,
    'data-section-active': isSectionActive || undefined,
    'data-nested': nested || undefined,
  }

  const inner = (
    <>
      {item.icon}
      {item.badge != null && item.badge > 0 && (
        <span
          title={item.badgeLabel}
          className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold text-primary-foreground tabular-nums shadow-sm"
        >
          <span aria-hidden={!!item.badgeLabel}>{item.badge > 99 ? '99+' : item.badge}</span>
          {item.badgeLabel && <span className="sr-only">{item.badgeLabel}</span>}
        </span>
      )}
      {showLabel && (
        <span className="block w-full text-center text-[9px] leading-tight">
          <span
            ref={labelRef}
            className={cn(
              'inline-block max-w-full overflow-hidden align-bottom whitespace-nowrap',
              labelOverflowing && '[mask-image:linear-gradient(to_right,black_75%,transparent)]',
              isActive ? 'font-medium' : '',
              nested && 'text-[8px]'
            )}
          >
            {item.label}
          </span>
        </span>
      )}
    </>
  )

  const trigger = item.href ? (
    <a
      {...dataProps}
      href={item.href}
      aria-label={item.badgeLabel ? `${item.label}: ${item.badgeLabel}` : item.label}
      aria-current={isActive ? 'page' : undefined}
      onClick={e => spaClick(e, () => onSelect?.(item.id))}
      className={className}
    >
      {inner}
    </a>
  ) : (
    <button
      {...dataProps}
      type="button"
      aria-label={item.badgeLabel ? `${item.label}: ${item.badgeLabel}` : item.label}
      aria-current={isActive ? 'page' : undefined}
      onClick={() => onSelect?.(item.id)}
      className={className}
    >
      {inner}
    </button>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  )
}

function Section({
  parent,
  childItems,
  activeId,
  isSectionActive,
  showLabels,
  onSelect,
}: {
  parent: NavigationRailItem
  childItems: NavigationRailItem[]
  activeId?: string
  isSectionActive: boolean
  showLabels?: boolean
  onSelect?: (id: string) => void
}) {
  const containsActive = childItems.some(i => i.id === activeId)
  const parentActive = parent.id === activeId

  // Allow a parent that doesn't have its own route (or whose onSelect is a
  // no-op) to still expand the section on click. Reset whenever activeId
  // changes so the section collapses when the user navigates outside it.
  const [userOpen, setUserOpen] = useState(false)
  useEffect(() => {
    setUserOpen(false)
  }, [activeId])

  const open = parent.alwaysOpen || containsActive || parentActive || userOpen

  const handleParentClick = (id: string) => {
    // Two-click model — first click on a closed parent expands the
    // submenu without navigating. Second click (parent already open via
    // userOpen / containsActive / parentActive) navigates as usual. This
    // lets the user reveal sub-items without being yanked away from
    // their current page.
    if (!open) {
      setUserOpen(true)
      return
    }
    if (!parent.nonNavigable) onSelect?.(id)
  }

  return (
    <div
      data-slot="navigation-rail-section"
      data-state={open ? 'open' : 'closed'}
      className={cn(
        'flex w-12 flex-col items-center rounded-xl p-1 transition-colors duration-200 ease-out',
        open && 'bg-muted'
      )}
    >
      <ItemButton
        item={parent}
        isActive={parent.id === activeId}
        isSectionActive={isSectionActive}
        showLabel={showLabels}
        onSelect={handleParentClick}
      />
      <div
        className={cn(
          'grid w-full transition-[grid-template-rows,opacity,margin] duration-200 ease-out',
          open ? 'mt-1 grid-rows-[1fr] opacity-100' : 'mt-0 grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col items-center gap-0.5">
            {childItems.map(item => (
              <ItemButton
                key={item.id}
                item={item}
                isActive={item.id === activeId}
                showLabel={showLabels}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function ItemList({
  items,
  activeId,
  showLabels,
  onSelect,
}: {
  items: NavigationRailItem[]
  activeId?: string
  showLabels?: boolean
  onSelect?: (id: string) => void
}) {
  type Node =
    | { kind: 'item'; item: NavigationRailItem }
    | { kind: 'section'; parent: NavigationRailItem; children: NavigationRailItem[] }

  const nodes: Node[] = []
  for (const item of items) {
    const last = nodes[nodes.length - 1]
    if (item.nested) {
      if (last && last.kind === 'item' && !last.item.nested) {
        nodes[nodes.length - 1] = { kind: 'section', parent: last.item, children: [item] }
      } else if (last && last.kind === 'section') {
        last.children.push(item)
      } else {
        // Orphan nested item with no parent — render as a plain item.
        nodes.push({ kind: 'item', item })
      }
    } else {
      nodes.push({ kind: 'item', item })
    }
  }

  return (
    <div className="flex w-full flex-col items-center gap-1">
      {nodes.map((node, ni) => {
        if (node.kind === 'section') {
          const sectionActive = node.children.some(c => c.id === activeId)
          return (
            <Section
              key={`section-${ni}-${node.parent.id}`}
              parent={node.parent}
              childItems={node.children}
              activeId={activeId}
              isSectionActive={sectionActive}
              showLabels={showLabels}
              onSelect={onSelect}
            />
          )
        }
        return (
          <div key={`item-${ni}-${node.item.id}`} className="contents">
            <ItemButton
              item={node.item}
              isActive={node.item.id === activeId}
              showLabel={showLabels}
              onSelect={onSelect}
            />
            {node.item.groupEnd && <Separator className="my-1 w-6" />}
          </div>
        )
      })}
    </div>
  )
}

function NavigationRail({
  items,
  footerItems,
  header,
  activeId,
  onSelect,
  variant = 'floating',
  showLabels = false,
  className,
  'aria-label': ariaLabel = 'Primary navigation',
}: NavigationRailProps) {
  const hasFooter = !!footerItems && footerItems.length > 0

  if (variant === 'floating') {
    const surface =
      'flex w-[76px] flex-col items-center rounded-2xl border border-border bg-sidebar p-2 shadow-sm'
    return (
      <TooltipProvider delayDuration={150}>
        <div
          data-slot="navigation-rail"
          data-variant="floating"
          aria-label={ariaLabel}
          role="navigation"
          className={cn('flex h-full flex-col items-center gap-2', className)}
        >
          <div className={surface}>
            {header && (
              <>
                <div data-slot="navigation-rail-header" className="flex flex-col items-center">
                  {header}
                </div>
                <Separator className="my-2 w-6" />
              </>
            )}
            <ItemList
              items={items}
              activeId={activeId}
              showLabels={showLabels}
              onSelect={onSelect}
            />
          </div>
          {hasFooter && (
            <div className={cn(surface, 'mt-auto')}>
              <ItemList
                items={footerItems}
                activeId={activeId}
                showLabels={showLabels}
                onSelect={onSelect}
              />
            </div>
          )}
        </div>
      </TooltipProvider>
    )
  }

  return (
    <TooltipProvider delayDuration={150}>
      <nav
        data-slot="navigation-rail"
        data-variant="static"
        aria-label={ariaLabel}
        className={cn(
          'flex h-full w-[76px] flex-col items-center border-r border-border bg-sidebar p-2',
          className
        )}
      >
        {header && (
          <>
            <div data-slot="navigation-rail-header" className="flex flex-col items-center">
              {header}
            </div>
            <Separator className="my-2 w-6" />
          </>
        )}
        <ItemList items={items} activeId={activeId} showLabels={showLabels} onSelect={onSelect} />
        {hasFooter && (
          <div className="mt-auto">
            <ItemList
              items={footerItems}
              activeId={activeId}
              showLabels={showLabels}
              onSelect={onSelect}
            />
          </div>
        )}
      </nav>
    </TooltipProvider>
  )
}

type NavigationRailCaseChipProps = {
  label: string
  initials?: string
  /** Icon shown on hover to signal the back-to-cases action. */
  hoverIcon?: React.ReactNode
  onBack?: () => void
  /** Localized "back to cases" suffix used in the tooltip & aria-label. */
  backLabel?: string
  /**
   * If provided, renders as `<a href>` so middle/cmd/ctrl-click open the
   * cases list in a new tab. Plain left-click is intercepted and routed
   * through `onBack` so SPA navigation still works.
   */
  href?: string
}

/**
 * Standard "case chip" rendered in the rail header when a case is open.
 * Clicking it navigates back to the cases list (global mode).
 */
function NavigationRailCaseChip({
  label,
  initials,
  hoverIcon,
  onBack,
  backLabel = 'back to cases',
  href,
}: NavigationRailCaseChipProps) {
  const className = cn(
    'group/chip relative flex size-10 items-center justify-center rounded-xl bg-muted text-sm font-semibold text-primary transition-colors outline-none',
    'hover:bg-accent',
    'focus-visible:ring-2 focus-visible:ring-ring',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5"
  )
  const sharedProps = {
    'data-slot': 'navigation-rail-case-chip',
    'aria-label': `${label} — ${backLabel}`,
    className,
  }
  const inner = (
    <>
      <span className="transition-opacity group-hover/chip:opacity-0">
        {initials ?? label.slice(0, 2).toUpperCase()}
      </span>
      {hoverIcon && (
        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover/chip:opacity-100">
          {hoverIcon}
        </span>
      )}
    </>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {href ? (
          <a {...sharedProps} href={href} onClick={e => spaClick(e, () => onBack?.())}>
            {inner}
          </a>
        ) : (
          <button {...sharedProps} type="button" onClick={onBack}>
            {inner}
          </button>
        )}
      </TooltipTrigger>
      <TooltipContent side="right">
        {label} — {backLabel}
      </TooltipContent>
    </Tooltip>
  )
}

export { NavigationRail, NavigationRailCaseChip, spaClick }
